import { useMemo, useState } from 'react';
import {
	Alert,
	Badge,
	Button,
	Descriptions,
	Drawer,
	Empty,
	Popconfirm,
	Skeleton,
	Space,
	Splitter,
	Switch,
	Table,
	Tabs,
	Tag,
	Tooltip,
	Typography,
	message,
} from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';

import {
	KeyenceComparisonFinding,
	KeyenceValidationComparisonRow,
	KeyenceValidationFinding,
	KeyenceValidationReport,
} from '../../interfaces/Quality.Interface';
import {
	useGetKeyenceValidationComparisonQuery,
	useGetKeyenceValidationReportQuery,
	useMarkKeyenceValidationFindingFalsePositiveMutation,
	useUnmarkKeyenceValidationFindingFalsePositiveMutation,
} from '../../redux/api/quality';
import {
	COMPARISON_STATUS,
	ENFORCEMENT_COLOR,
	FIELD_LABELS,
	OUTCOME_COLOR,
	SEVERITY_COLOR,
	findingCodeLabel,
} from './constants';
import ValidationDrawingPanel, { resolveBalloonId } from './ValidationDrawingPanel';
import { useGetBallooningProjectQuery } from '../../redux/api/ballooning';
import { Balloon } from '../../interfaces/Ballooning';
import './ReportDrawer.css';

const { Text, Paragraph } = Typography;

const TabLabelWithBadge = ({
	title,
	count,
	loading = false,
}: {
	title: string;
	count: number;
	loading?: boolean;
}) => (
	<span className="validation-report-tab-label">
		{title}
		{!loading && count > 0 && (
			<Badge count={count} size="small" className="validation-report-tab-badge" />
		)}
	</span>
);

interface Props {
	report: KeyenceValidationReport | null;
	onClose: () => void;
}

interface BalloonSelectionProps {
	activeBalloonId: string | null;
	onSelectBalloon: (balloonId: string | null) => void;
	balloonsByNumber: Map<number, Balloon>;
}

const dash = <Text type="secondary">—</Text>;

const unitWord = (unit: string | null) =>
	unit === 'MM' ? 'millimetres' : 'inches';

const value = (raw: string | null, highlight = false) => {
	if (raw === null || raw === undefined) return dash;
	return highlight ? <Text type="danger">{raw}</Text> : <span>{raw}</span>;
};

const FindingsTab = ({
	reportId,
	activeBalloonId,
	onSelectBalloon,
	balloonsByNumber,
}: { reportId: string } & BalloonSelectionProps) => {
	const { data, isFetching } = useGetKeyenceValidationReportQuery(reportId);
	const [markFalsePositive, { isLoading: marking }] =
		useMarkKeyenceValidationFindingFalsePositiveMutation();
	const [unmarkFalsePositive, { isLoading: unmarking }] =
		useUnmarkKeyenceValidationFindingFalsePositiveMutation();
	const [showFalsePositives, setShowFalsePositives] = useState(false);
	const [actingFindingId, setActingFindingId] = useState<string | null>(null);

	const allFindings = data?.findings ?? [];
	const falsePositiveCount = allFindings.filter((f) => f.is_false_positive).length;
	const visibleFindings = useMemo(
		() =>
			showFalsePositives
				? allFindings
				: allFindings.filter((finding) => !finding.is_false_positive),
		[allFindings, showFalsePositives]
	);

	const handleMarkFalsePositive = async (findingId: string) => {
		setActingFindingId(findingId);
		try {
			await markFalsePositive({ reportId, findingId }).unwrap();
			message.success('Finding marked as false positive');
		} catch {
			message.error('Could not mark finding as false positive');
		} finally {
			setActingFindingId(null);
		}
	};

	const handleUnmarkFalsePositive = async (findingId: string) => {
		setActingFindingId(findingId);
		try {
			await unmarkFalsePositive({ reportId, findingId }).unwrap();
			message.success('Finding restored');
		} catch {
			message.error('Could not restore finding');
		} finally {
			setActingFindingId(null);
		}
	};

	// When a misnamed measurement was matched to a missing balloon, both
	// messages already spell the link out, so the hint below would only repeat
	// them. It stays for suggestions the messages do not mention.
	const linkedLabels = new Set(
		allFindings
			.map((finding) => finding.suggested_program_label)
			.filter((label): label is string => label !== null)
	);

	const handleSelectFinding = (finding: KeyenceValidationFinding) => {
		const balloonId = resolveBalloonId(finding, balloonsByNumber);
		onSelectBalloon(balloonId);
	};

	const findingRowClassName = (row: KeyenceValidationFinding) => {
		const balloonId = resolveBalloonId(row, balloonsByNumber);
		const classes: string[] = [];
		if (row.is_false_positive) classes.push('validation-report-row--muted');
		if (balloonId && balloonId === activeBalloonId) {
			classes.push('validation-report-row--selected');
		}
		return classes.join(' ');
	};

	const columns: ColumnsType<KeyenceValidationFinding> = [
		{
			title: 'Severity',
			dataIndex: 'severity',
			width: 100,
			render: (severity: KeyenceValidationFinding['severity'], row) => (
				<Space size={4} wrap>
					<Tag color={SEVERITY_COLOR[severity]}>{severity}</Tag>
					{row.is_false_positive && <Tag color="purple">False positive</Tag>}
				</Space>
			),
		},
		{
			title: 'Issue',
			dataIndex: 'code',
			width: 210,
			render: (code: string) => findingCodeLabel(code),
		},
		{
			title: 'Balloon',
			dataIndex: 'balloon_number',
			width: 90,
			render: (number: number | null, row) =>
				number ?? (row.program_label ? `"${row.program_label}"` : dash),
		},
		{
			title: 'Field',
			dataIndex: 'field',
			width: 130,
			render: (field: string | null) =>
				field ? FIELD_LABELS[field] ?? field : dash,
		},
		{
			title: 'Expected',
			dataIndex: 'expected_value',
			width: 110,
			render: (raw: string | null) => value(raw),
		},
		{
			title: 'In program',
			dataIndex: 'actual_value',
			width: 110,
			render: (raw: string | null, row) =>
				row.code === 'MISSING_FROM_PROGRAM' ? (
					<Text type="danger">Not in program</Text>
				) : (
					value(raw, true)
				),
		},
		{
			title: 'Detail',
			dataIndex: 'message',
			render: (message: string, row) => (
				<div>
					<div>{message}</div>
					{row.suggested_balloon_number !== null &&
						!linkedLabels.has(row.program_label ?? '') && (
							<Text type="secondary">
								Probably meant balloon {row.suggested_balloon_number}
								{row.confidence_band ? ` (${row.confidence_band} confidence)` : ''}
							</Text>
						)}
				</div>
			),
		},
		{
			title: '',
			width: 130,
			fixed: 'right',
			render: (_, row) => {
				const loading =
					actingFindingId === row.id && (marking || unmarking);
				if (row.is_false_positive) {
					return (
						<Popconfirm
							title="Restore this finding?"
							description="It will count toward the report again."
							onConfirm={() => handleUnmarkFalsePositive(row.id)}
							okText="Restore"
						>
							<Button
								size="small"
								loading={loading}
								onClick={(e) => e.stopPropagation()}
							>
								Restore
							</Button>
						</Popconfirm>
					);
				}
				return (
					<Popconfirm
						title="Mark as false positive?"
						description="This finding will be hidden from the results table."
						onConfirm={() => handleMarkFalsePositive(row.id)}
						okText="Mark false positive"
					>
						<Button
							size="small"
							loading={loading}
							onClick={(e) => e.stopPropagation()}
						>
							False positive
						</Button>
					</Popconfirm>
				);
			},
		},
	];

	if (isFetching && !data) return <Skeleton active />;

	const emptyDescription =
		allFindings.length === 0
			? 'No findings — the program matched the plan'
			: falsePositiveCount === allFindings.length
				? 'All findings were marked as false positives'
				: 'No active findings';

	return (
		<Space direction="vertical" size={12} style={{ width: '100%' }}>
			{falsePositiveCount > 0 && (
				<Space size={8}>
					<Switch
						checked={showFalsePositives}
						onChange={setShowFalsePositives}
					/>
					<Text>
						Show false positives
						<Text type="secondary"> ({falsePositiveCount})</Text>
					</Text>
				</Space>
			)}
			<Table
				className="validation-report-table"
				size="small"
				rowKey="id"
				loading={isFetching}
				dataSource={visibleFindings}
				columns={columns}
				pagination={false}
				rowClassName={findingRowClassName}
				onRow={(row) => {
					const balloonId = resolveBalloonId(row, balloonsByNumber);
					return {
						onClick: () => handleSelectFinding(row),
						style: { cursor: balloonId ? 'pointer' : 'default' },
					};
				}}
				locale={{
					emptyText: (
						<Empty
							image={Empty.PRESENTED_IMAGE_SIMPLE}
							description={emptyDescription}
						/>
					),
				}}
			/>
		</Space>
	);
};

const ComparisonNotes = ({ findings }: { findings: KeyenceComparisonFinding[] }) => {
	if (findings.length === 0) return dash;
	return (
		<div className="flex flex-col gap-1">
			{findings.map((finding, index) => (
				<Text key={`${finding.code}-${finding.field}-${index}`}>
					{finding.message}
				</Text>
			))}
		</div>
	);
};

const ComparisonTab = ({
	reportId,
	activeBalloonId,
	onSelectBalloon,
	balloonsByNumber,
}: { reportId: string } & BalloonSelectionProps) => {
	const { data, isFetching } = useGetKeyenceValidationComparisonQuery(reportId);

	const handleSelectRow = (row: KeyenceValidationComparisonRow) => {
		const balloonId =
			row.plan?.balloon_id ??
			(row.balloon_number !== null
				? balloonsByNumber.get(row.balloon_number)?.id ?? null
				: null);
		onSelectBalloon(balloonId);
	};

	const comparisonStatusClass = (
		status: KeyenceValidationComparisonRow['status']
	) => {
		if (status === 'mismatch' || status === 'missing_from_program') {
			return 'validation-report-row--status-error';
		}
		if (
			status === 'not_in_plan' ||
			status === 'misnamed' ||
			status === 'duplicate'
		) {
			return 'validation-report-row--status-warning';
		}
		return '';
	};

	const comparisonRowClassName = (row: KeyenceValidationComparisonRow) => {
		const balloonId =
			row.plan?.balloon_id ??
			(row.balloon_number !== null
				? balloonsByNumber.get(row.balloon_number)?.id ?? null
				: null);
		const classes = [comparisonStatusClass(row.status)];
		if (balloonId && balloonId === activeBalloonId) {
			classes.push('validation-report-row--selected');
		}
		return classes.filter(Boolean).join(' ');
	};

	const columns: ColumnsType<KeyenceValidationComparisonRow> = [
		{
			title: 'Balloon',
			dataIndex: 'balloon_number',
			width: 90,
			fixed: 'left',
			render: (number: number | null, row) => {
				// A paired row belongs to a balloon the program never named, so
				// both the balloon and the name actually used have to show.
				if (row.status === 'probable_rename') {
					return (
						<div>
							<div>{number}</div>
							<Text type="secondary" className="text-xs">
								"{row.program_label}"
							</Text>
						</div>
					);
				}
				return number ?? (row.program_label ? `"${row.program_label}"` : dash);
			},
		},
		{
			title: 'Status',
			dataIndex: 'status',
			width: 130,
			render: (status: KeyenceValidationComparisonRow['status']) => {
				const meta = COMPARISON_STATUS[status];
				return (
					<Tooltip title={meta.hint}>
						<Tag color={meta.color}>{meta.label}</Tag>
					</Tooltip>
				);
			},
		},
		{
			title: 'Program',
			children: [
				{
					title: 'Type',
					width: 100,
					render: (_, row) => row.program?.measurement_type ?? dash,
				},
				{
					title: 'Nominal',
					width: 100,
					render: (_, row) =>
						value(
							row.program?.nominal ?? null,
							row.mismatched_fields.includes('nominal')
						),
				},
				{
					title: '+Tol',
					width: 100,
					render: (_, row) =>
						value(
							row.program?.tolerance_plus ?? null,
							row.mismatched_fields.includes('tolerance_plus')
						),
				},
				{
					title: '-Tol',
					width: 100,
					render: (_, row) =>
						value(
							row.program?.tolerance_minus ?? null,
							row.mismatched_fields.includes('tolerance_minus')
						),
				},
			],
		},
		{
			title: 'Inspection plan',
			children: [
				{
					title: 'Type',
					width: 130,
					render: (_, row) => row.plan?.gdt_type ?? dash,
				},
				{
					title: 'Nominal',
					width: 100,
					render: (_, row) => value(row.plan?.nominal ?? null),
				},
				{
					title: '+Tol',
					width: 100,
					render: (_, row) => value(row.plan?.tolerance_plus ?? null),
				},
				{
					title: '-Tol',
					width: 100,
					render: (_, row) => value(row.plan?.tolerance_minus ?? null),
				},
			],
		},
		{
			title: 'Notes',
			width: 320,
			render: (_, row) => <ComparisonNotes findings={row.findings} />,
		},
	];

	if (isFetching && !data) return <Skeleton active />;
	if (!data) return null;

	return (
		<div className="flex flex-col gap-3">
			{data.plan_modified_since_validation && (
				<Alert
					type="warning"
					showIcon
					message="The inspection plan changed after this report was generated"
					description="Program values are from the checked file, but plan values are read live. Differences here may reflect a later plan edit rather than a programming mistake."
				/>
			)}
			{!data.values_comparable && (
				<Alert
					type="info"
					showIcon
					message="Values were not compared"
					description="The ballooning project has no unit set, so nominals and tolerances could not be converted."
				/>
			)}
			{data.report_findings.length > 0 && (
				<Alert
					type="info"
					showIcon
					message="Applies to the whole program"
					description={
						<div className="flex flex-col gap-1">
							{data.report_findings.map((finding, index) => (
								<span key={`${finding.code}-${index}`}>{finding.message}</span>
							))}
						</div>
					}
				/>
			)}
			<Table
				className="validation-report-table"
				size="small"
				rowKey="key"
				bordered
				loading={isFetching}
				dataSource={data.rows}
				columns={columns}
				pagination={false}
				scroll={{ x: 1400 }}
				rowClassName={comparisonRowClassName}
				onRow={(row) => {
					const balloonId =
						row.plan?.balloon_id ??
						(row.balloon_number !== null
							? balloonsByNumber.get(row.balloon_number)?.id ?? null
							: null);
					return {
						onClick: () => handleSelectRow(row),
						style: { cursor: balloonId ? 'pointer' : 'default' },
					};
				}}
				locale={{
					emptyText: (
						<Empty
							image={Empty.PRESENTED_IMAGE_SIMPLE}
							description="Nothing to compare — the program had no decodable measurements and the plan had no Keyence balloons for this view and device"
						/>
					),
				}}
			/>
			{data.values_comparable && (
				<Text type="secondary">
					Values are shown in {unitWord(data.unit)}.
					{data.declared_unit !== data.unit &&
						` The ballooning project is set to ${unitWord(
							data.declared_unit
						)}, which this report found to be wrong, so these rows use the unit the plan's numbers are actually in.`}
				</Text>
			)}
		</div>
	);
};

const ReportDrawer = ({ report, onClose }: Props) => {
	const [activeBalloonId, setActiveBalloonId] = useState<string | null>(null);
	const [centerOnActive, setCenterOnActive] = useState(false);
	const [pageNumber, setPageNumber] = useState(1);

	const { data: detail, isFetching: detailFetching } =
		useGetKeyenceValidationReportQuery(report?.id ?? '', {
			skip: !report,
		});
	const displayReport = detail ?? report;
	const { data: comparisonData } = useGetKeyenceValidationComparisonQuery(
		displayReport?.id ?? '',
		{ skip: !displayReport?.id }
	);
	const activeFindingCount =
		(displayReport?.error_count ?? 0) + (displayReport?.warning_count ?? 0);
	const comparisonIssueCount = useMemo(
		() =>
			comparisonData?.rows.filter((row) => row.status !== 'match').length ?? 0,
		[comparisonData]
	);

	const projectId = displayReport?.ballooning_project_id ?? '';
	const { data: projectData } = useGetBallooningProjectQuery(
		{ id: projectId },
		{ skip: !projectId }
	);

	const balloonsByNumber = useMemo(() => {
		const map = new Map<number, Balloon>();
		if (!projectData?.ballooning_pages || !displayReport) return map;
		projectData.ballooning_pages
			.flatMap((page) => page.balloons ?? [])
			.filter(
				(balloon) =>
					balloon.measurement_strategy === 'Keyence' &&
					(!displayReport.view || balloon.view === displayReport.view)
			)
			.forEach((balloon) => map.set(balloon.balloon_number, balloon));
		return map;
	}, [projectData, displayReport]);

	const handleSelectBalloon = (balloonId: string | null) => {
		if (!balloonId) return;
		setActiveBalloonId(balloonId);
		setCenterOnActive(true);
	};

	const handleClose = () => {
		setActiveBalloonId(null);
		setCenterOnActive(false);
		setPageNumber(1);
		onClose();
	};

	const balloonSelectionProps: BalloonSelectionProps = {
		activeBalloonId,
		onSelectBalloon: handleSelectBalloon,
		balloonsByNumber,
	};

	return (
	<Drawer
		open={report !== null}
		onClose={handleClose}
		width="min(1400px, 98vw)"
		destroyOnClose
		title={
			displayReport && (
				<Space wrap>
					<span>{displayReport.file_name}</span>
					<Tag color={OUTCOME_COLOR[displayReport.outcome]}>{displayReport.outcome}</Tag>
					{displayReport.enforcement !== 'Accepted' && (
						<Tag color={ENFORCEMENT_COLOR[displayReport.enforcement]}>
							{displayReport.enforcement}
						</Tag>
					)}
				</Space>
			)
		}
	>
		{displayReport && (
			<>
				<Descriptions size="small" column={{ xs: 1, sm: 2, lg: 3 }} bordered>
					<Descriptions.Item label="Part">
						{displayReport.part_name ?? '—'}
					</Descriptions.Item>
					<Descriptions.Item label="Order">
						{displayReport.order_name ?? '—'}
					</Descriptions.Item>
					<Descriptions.Item label="Inspection plan">
						{displayReport.ballooning_project_id ? (
							<Link to={`/ballooning/${displayReport.ballooning_project_id}`}>
								<Space size={4}>
									<LinkOutlined />
									Open inspection plan
								</Space>
							</Link>
						) : (
							'—'
						)}
					</Descriptions.Item>
					<Descriptions.Item label="View">
						{displayReport.view ?? '—'}
					</Descriptions.Item>
					<Descriptions.Item label="Device">
						{displayReport.keyence_device_name ?? '—'}
					</Descriptions.Item>
					<Descriptions.Item label="Uploaded by">
						{displayReport.uploaded_by_name ?? '—'}
					</Descriptions.Item>
					<Descriptions.Item label="Checked">
						{dayjs(displayReport.created_time).format('MMM D, YYYY h:mm A')}
					</Descriptions.Item>
					<Descriptions.Item label="Coverage">
						{displayReport.matched_count} of {displayReport.plan_balloon_count} plan balloons
						matched, {displayReport.program_measurement_count} measured
					</Descriptions.Item>
					<Descriptions.Item label="Source">{displayReport.source}</Descriptions.Item>
					<Descriptions.Item label="Parser">
						{displayReport.parser_version
							? `${displayReport.parser_version} (schema ${displayReport.parser_schema_version ?? '?'})`
							: '—'}
					</Descriptions.Item>
				</Descriptions>

				{displayReport.override_reason && (
					<Alert
						className="mt-4"
						type="warning"
						showIcon
						message="Upload overridden"
						description={displayReport.override_reason}
					/>
				)}
				{displayReport.parse_error && (
					// Only 'Parse Error' means something actually failed. A
					// program skipped for size uploaded fine and simply was not
					// read, so showing it in red made a healthy upload look broken.
					<Alert
						className="mt-4"
						type={displayReport.outcome === 'Parse Error' ? 'error' : 'info'}
						showIcon
						message={
							displayReport.outcome === 'Parse Error'
								? 'The file could not be parsed'
								: 'This program was not validated'
						}
						description={<Paragraph>{displayReport.parse_error}</Paragraph>}
					/>
				)}

				<Splitter layout="vertical" style={{ height: 'calc(100vh - 220px)', minHeight: 520 }}>
					<Splitter.Panel defaultSize="45%" min="25%">
						{displayReport.ballooning_project_id ? (
							<ValidationDrawingPanel
								report={displayReport}
								findings={detail?.findings ?? []}
								activeBalloonId={activeBalloonId}
								setActiveBalloonId={setActiveBalloonId}
								centerOnActive={centerOnActive}
								setCenterOnActive={setCenterOnActive}
								pageNumber={pageNumber}
								setPageNumber={setPageNumber}
							/>
						) : (
							<Empty
								image={Empty.PRESENTED_IMAGE_SIMPLE}
								description="No inspection plan is linked to this report"
							/>
						)}
					</Splitter.Panel>
					<Splitter.Panel min="30%" className="validation-report-tabs-panel">
						<Tabs
							className="validation-report-tabs"
							items={[
								{
									key: 'findings',
									label: (
										<TabLabelWithBadge
											title="Findings"
											count={activeFindingCount}
											loading={detailFetching && !detail}
										/>
									),
									children: (
										<FindingsTab
											reportId={displayReport.id}
											{...balloonSelectionProps}
										/>
									),
								},
								{
									key: 'comparison',
									label: (
										<TabLabelWithBadge
											title="Program vs plan"
											count={comparisonIssueCount}
										/>
									),
									children: (
										<ComparisonTab
											reportId={displayReport.id}
											{...balloonSelectionProps}
										/>
									),
								},
							]}
						/>
					</Splitter.Panel>
				</Splitter>
			</>
		)}
	</Drawer>
	);
};

export default ReportDrawer;
